import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLanguage } from "@/lib/i18n";

const STATUS_META = {
  pending: { chip: "ap-chip-amber", label: "Pending" },
  pending_verification: { chip: "ap-chip-amber", label: "Pending Verification" },
  failed: { chip: "ap-chip-red", label: "Failed" },
  paid: { chip: "ap-chip-green", label: "Paid" },
  fee_waived: { chip: "ap-chip-gold", label: "Fee Waived" },
};

export default function RegistrationPaymentCard({ payLabel }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [payment, setPayment] = useState(undefined); // undefined = loading, null = none
  const [upi, setUpi] = useState(null);
  const [utr, setUtr] = useState("");
  const [payerName, setPayerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const refresh = useCallback(async () => {
    try {
      const d = await api.get("/payments/mine");
      setPayment(d.payment);
    } catch (e) {
      setPayment(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    api.get("/payments/upi").then(setUpi).catch(() => setUpi({ configured: false }));
  }, [refresh]);

  const submitUtr = async () => {
    setError("");
    setBusy(true);
    try {
      await api.post("/payments/upi/submit", { utr: utr.trim(), payerName: payerName.trim() });
      setUtr("");
      setPayerName("");
      await refresh();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(upi.upiId);
      setCopied("copied");
    } catch (e) {
      setCopied("failed");
    }
    setTimeout(() => setCopied(""), 2500);
  };

  const meta = payment ? STATUS_META[payment.status] || STATUS_META.pending : null;

  // Offer-aware amounts: the member's own fee record wins; fall back to the current offer price.
  const orig = payment ? payment.originalAmount || 500 : upi?.originalAmount || 500;
  const amt = payment ? payment.finalAmount || 500 : upi?.amount || 500;
  const offerActive = payment
    ? !!(payment.offer && payment.offer.discount)
    : !!(upi && upi.offerActive && upi.offer && upi.offer.discount);
  const discount = offerActive ? orig - amt : 0;

  // Standard UPI deep link — built from SERVER-configured details + the dynamic payable amount.
  const upiHref = upi?.upiId
    ? `upi://pay?pa=${encodeURIComponent(upi.upiId)}&pn=${encodeURIComponent(upi.payeeName || "")}&am=${amt}&cu=INR&tn=${encodeURIComponent("Suffa Matrimony Registration Fee")}`
    : "";

  return (
    <section className="cp-card" data-testid="registration-payment-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h3 className="ap-card-title" style={{ margin: 0 }} data-testid="payment-fee-title">
          {t(`Registration Fee: ₹${orig}`)}
        </h3>
        {payment && meta && (
          <span className={`ap-chip ${meta.chip}`} data-testid="payment-status-chip">
            {t(meta.label)}
          </span>
        )}
      </div>

      {payment === undefined && <p className="cp-subtitle" data-testid="payment-loading">{t("Loading…")}</p>}
      {payment === null && (
        <p className="cp-subtitle" data-testid="payment-none" style={{ margin: "8px 0 0" }}>
          {t("Complete your profile to see your registration fee status.")}
        </p>
      )}

      {payment && payment.status === "paid" && (
        <p className="cp-success-text" data-testid="payment-success-note" style={{ margin: "10px 0 0" }}>
          {t("Payment Successful — thank you! Your profile is now pending admin approval.")}
        </p>
      )}
      {payment && payment.status === "fee_waived" && (
        <p className="cp-success-text" data-testid="payment-waived-note" style={{ margin: "10px 0 0" }}>
          {t("Fee Waived — the ₹500 registration fee was waived by Suffa Matrimony. No payment is required.")}
        </p>
      )}
      {payment && payment.upiRejectionNote && (
        <p className="cp-error" data-testid="payment-rejected-note" style={{ margin: "10px 0 0" }}>
          {t("Payment rejected:")} {payment.upiRejectionNote} {t("Please pay again and submit a fresh UTR, or contact support.")}
        </p>
      )}

      {payment && payment.status === "pending_verification" && (
        <p className="cp-success-text" data-testid="payment-verification-note" style={{ margin: "10px 0 0" }}>
          {t("Payment submitted – Awaiting admin verification. Our team will confirm the credit in the bank account shortly, insha'Allah.")}
        </p>
      )}

      {payment && (payment.status === "pending" || payment.status === "failed") && (
        <>
          <p className="cp-subtitle" style={{ margin: "8px 0 12px" }} data-testid="payment-instructions">
            {t(`Browsing is free. This one-time ₹${amt} fee submits your profile for admin approval. Your profile becomes visible in Find Matches only after the fee is verified and an admin approves it.`)}
          </p>
          {offerActive && discount > 0 && (
            <div data-testid="payment-offer-breakdown" style={{ display: "flex", gap: 16, flexWrap: "wrap", background: "rgba(6,78,59,0.06)", border: "1px solid rgba(201,162,75,0.5)", borderRadius: 10, padding: "10px 14px", margin: "0 0 12px", fontSize: 14 }}>
              <span data-testid="payment-regular-fee">
                {t("Regular fee")}: <s style={{ opacity: 0.6 }}>₹{orig}</s>
              </span>
              <span data-testid="payment-discount" style={{ color: "#064E3B", fontWeight: 600 }}>
                {t("Launch discount (20%)")}: −₹{discount}
              </span>
              <span data-testid="payment-final-amount" style={{ fontWeight: 700 }}>
                {t("Payable now")}: ₹{amt}
              </span>
            </div>
          )}
          {upi && upi.configured ? (
            <div data-testid="upi-section">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", margin: "0 0 14px" }}>
                <a className="btn btn-solid btn-lg" data-testid="upi-pay-btn" href={upiHref}>
                  {t(`Pay ₹${amt} with UPI`)}
                </a>
                <button type="button" className="btn btn-outline btn-lg" data-testid="copy-upi-id" onClick={copyUpi}>
                  {copied === "copied" ? t("Copied!") : t("Copy UPI ID")}
                </button>
              </div>
              <p className="ap-note" data-testid="upi-fallback-note" style={{ margin: "0 0 14px" }}>
                {t("Nothing opened? Your phone may have no UPI app — scan the QR or copy the UPI ID, pay from another phone, then submit the UTR here.")}
              </p>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
                {upi.qrImage && (
                  <img
                    src={upi.qrImage} alt="UPI payment QR" data-testid="upi-qr-image"
                    style={{ width: 190, height: 264, objectFit: "contain", border: "1px solid rgba(28,25,23,0.15)", borderRadius: 10, padding: 6, background: "#fff" }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 240 }}>
                  <p className="ap-card-title" style={{ margin: 0, fontSize: 15 }}>{t("Pay via UPI QR")}</p>
                  <p className="cp-subtitle" style={{ margin: "6px 0" }}>
                    {upi.payeeName ? `${t(`Pay exactly ₹${amt} to`)} ${upi.payeeName}` : t(`Pay exactly ₹${amt} to`)}
                    {upi.upiId ? <> — <strong data-testid="upi-id">{upi.upiId}</strong>.</> : "."}
                  </p>
                  <p className="cp-subtitle" style={{ margin: "0 0 6px" }} data-testid="upi-scan-instructions">
                    {t(`Scan the QR using Google Pay, PhonePe or any UPI app and pay exactly ₹${amt}. Then enter the UTR / UPI Transaction ID shown in your payment app receipt below.`)}
                  </p>
                  {upi.payeeName && (
                    <p className="ap-note" data-testid="upi-recipient-note" style={{ margin: "0 0 10px" }}>
                      {t("The payment will appear as paid to")} <strong>{upi.payeeName}</strong> — {t("our current QR is temporary and will be replaced by a Suffa Matrimony merchant QR later.")}
                    </p>
                  )}
                  <input
                    className="ap-input" data-testid="payer-name-input" value={payerName} maxLength={60}
                    placeholder={t("Payer name on UPI app (optional)")}
                    onChange={(e) => setPayerName(e.target.value)}
                    style={{ maxWidth: 340, marginBottom: 10 }}
                  />
                  <br />
                  <input
                    className="ap-input" data-testid="utr-input" value={utr} maxLength={22}
                    placeholder={t("Enter UTR (UPI reference number)")}
                    onChange={(e) => setUtr(e.target.value)}
                    style={{ maxWidth: 340, marginBottom: 10 }}
                  />
                  <br />
                  <button type="button" className="btn btn-solid btn-lg" data-testid="utr-submit-btn" disabled={busy || utr.trim().length < 8} onClick={submitUtr}>
                    {busy ? t("Submitting…") : t("Submit for Verification")}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p className="ap-note" data-testid="upi-not-configured" style={{ marginBottom: 0 }}>
              {t(`UPI payment is being set up. Please contact support to pay the ₹${amt} registration fee.`)}
            </p>
          )}
        </>
      )}

      {error && (
        <p className="cp-error" data-testid="payment-error" role="alert" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}
    </section>
  );
}
